import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import {
  Block,
  Empty,
  Field,
  Input,
  NumInput,
  Pill,
  Segment,
  Select,
  Textarea,
  useToast,
} from '../ui';
import { PhotoGrid } from '../media/MediaGrid';
import useMedia from '../media/useMedia';
import { REVIEW_SECTIONS } from '../data/reviewSchema';
import { FINAL_DECISIONS } from '../data/constants';
import { landed, landedPrefill, riskFlags, money } from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * Final Review (spec § 5.12) — the "after China" screen.
 *
 * Ten collapsible sections, 82 fields, 400ms autosave, with the landed cost
 * recomputing live at the top. Section A opens by default because it is the one
 * that produces the number everything else is judged against.
 *
 * On desktop the sections tile into columns (.ft-review-cols) — they are
 * independent, and ten stacked accordions is a lot of scrolling at a desk.
 */
export default function ReviewScreen() {
  const { id } = useParams();
  const toast = useToast();
  const { fairs, suppliers, saveSupplier } = useFairTracker();

  const supplier = suppliers.find((s) => s.id === id) || null;
  const fair = supplier ? fairs.find((f) => f.id === supplier.fairId) || null : null;

  /* The form is held locally and flushed on a debounce, so typing never waits
     on IndexedDB or re-renders the whole screen per keystroke. */
  const [review, setReview] = useState(() => (supplier && supplier.review) || {});
  const [decision, setDecision] = useState(() => (supplier && supplier.decision) || '');
  const timer = useRef(null);
  const prefilled = useRef(false);

  const media = useMedia({ supplierId: id });

  /* Prefill section A from the quoted price and MOQ (spec § 5.12), once, and
     only into fields the buyer has not filled in themselves. */
  useEffect(() => {
    if (!supplier || prefilled.current) return;
    prefilled.current = true;
    const pre = landedPrefill(supplier, fair);
    setReview((r) => ({
      ...r,
      tc_price: r.tc_price || pre.tc_price,
      tc_qty: r.tc_qty || pre.tc_qty,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier && supplier.id]);

  /* 400ms autosave. */
  useEffect(() => {
    if (!supplier) return undefined;
    const unchanged =
      JSON.stringify(review) === JSON.stringify(supplier.review || {}) &&
      decision === (supplier.decision || '');
    if (unchanged) return undefined;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      saveSupplier({ ...supplier, review, decision: decision || null }).catch(() =>
        toast('Could not save on this device'),
      );
    }, 400);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review, decision]);

  if (!supplier) {
    return (
      <div className="ft-page">
        <Empty
          title={tr("That supplier is gone")}
          action={
            <Link className="ft-btn" to={p('/shortlist')}>
              {tr("Back to the shortlist")}
            </Link>
          }
        />
      </div>
    );
  }

  const set = (k) => (v) => setReview((r) => ({ ...r, [k]: v }));
  const working = { ...supplier, review };
  const l = landed(working);
  const flags = riskFlags(working);
  const product = (supplier.products || [])[0] || null;

  const renderField = ([key, label, type, options]) => {
    const value = review[key] == null ? '' : review[key];
    if (type === 'select') {
      return (
        <Field key={key} label={label}>
          <Select value={value} options={options} onChange={set(key)} />
        </Field>
      );
    }
    if (type === 'seg') {
      return (
        <Field key={key} label={label}>
          <Segment value={value} options={options} onChange={set(key)} tone="decision" />
        </Field>
      );
    }
    if (type === 'textarea') {
      return (
        <Field key={key} label={label}>
          <Textarea value={value} onChange={set(key)} rows={3} />
        </Field>
      );
    }
    if (type === 'number') {
      return (
        <Field key={key} label={label}>
          <NumInput value={value} onChange={set(key)} />
        </Field>
      );
    }
    return (
      <Field key={key} label={label}>
        <Input type={type === 'date' ? 'date' : 'text'} value={value} onChange={set(key)} />
      </Field>
    );
  };

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{supplier.company}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {[product && product.name, fair && fair.name].filter(Boolean).join(' · ')}
        </p>
      </div>

      <div className="ft-row">
        <Link className="ft-btn" to={p(`/s/${supplier.id}`)}>
          {tr("Profile")}
        </Link>
        <Link className="ft-btn" to={`${p('/compare')}?ids=${supplier.id}`}>
          {tr("Compare")}
        </Link>
        <Link className="ft-btn" to={p('/shortlist')}>
          {tr("Back to shortlist")}
        </Link>
      </div>

      {/* The landed cost, live. This is the number the decision rests on. */}
      <section className="ft-card ft-landed ft-landed-big" style={{ marginTop: 12 }}>
        <div>
          <span>{tr("Total landed cost")}</span>
          <b>{l.total ? money(l.total) : '—'}</b>
        </div>
        <div>
          <span>Landed cost per unit{l.qty ? ` (÷ ${l.qty} units)` : ''}</span>
          <b>{l.perUnit != null ? money(l.perUnit) : '—'}</b>
        </div>
      </section>
      {l.perUnit == null && l.total > 0 && (
        <p className="ft-hint">{tr("Set a quantity in section A to get a per-unit figure.")}</p>
      )}

      <div className="ft-review-cols" style={{ marginTop: 12 }}>
        {REVIEW_SECTIONS.map((section) => (
          <Block
            key={section.id}
            title={section.title}
            defaultOpen={!!section.defaultOpen}
            badge={filledCount(section, review)}
          >
            <div className="ft-grid-2">{section.fields.map(renderField)}</div>

            {section.uploads && (
              <>
                <p className="ft-hint" style={{ marginTop: 8 }}>
                  {tr("Attach documents")}
                </p>
                <div className="ft-chips">
                  {section.uploadTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className="ft-chip"
                      onClick={async () => {
                        const next = await media.addPhotos({
                          existing: review[section.uploads] || [],
                          tag,
                        });
                        setReview((r) => ({ ...r, [section.uploads]: next }));
                      }}
                    >
                      <Icon name="Upload" size={14} /> {tag}
                    </button>
                  ))}
                </div>
                <PhotoGrid
                  photos={review[section.uploads] || []}
                  onRemove={async (ph) => {
                    const next = await media.remove(review[section.uploads] || [], ph);
                    setReview((r) => ({ ...r, [section.uploads]: next }));
                  }}
                />
              </>
            )}
          </Block>
        ))}
      </div>

      {/* ── Final decision ──────────────────────────────────────────────── */}
      <section className="ft-card" style={{ marginTop: 14 }}>
        <h2 className="ft-h2">{tr("Final decision")}</h2>

        {flags.length > 0 ? (
          <>
            <p className="ft-sub">
              {flags.length} risk flag{flags.length === 1 ? '' : 's'} — these are gaps in what you
              know, not verdicts.
            </p>
            <ul className="ft-flags">
              {flags.map((f) => (
                <li key={f}>
                  <Icon name="AlertTriangle" size={12} /> {f}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="ft-sub">
            <Icon name="CheckCircle" size={13} /> Nothing outstanding — sample approved,
            certification verified, licence and HS code on file.
          </p>
        )}

        <Field label={tr("Decision")} className="ft-span2">
          <Segment value={decision} options={FINAL_DECISIONS} onChange={setDecision} tone="decision" />
        </Field>
        <Field label={tr("Reason")}>
          <Textarea value={review.final_reason || ''} onChange={set('final_reason')} rows={3} />
        </Field>
        <Field label={tr("Next action")}>
          <Input value={review.final_next || ''} onChange={set('final_next')} />
        </Field>

        {decision && (
          <p className="ft-sub" style={{ marginTop: 8 }}>
            {tr("Saved as")} <Pill>{decision}</Pill>
          </p>
        )}
      </section>
    </div>
  );
}

/** "3/10" badge so a collapsed section still shows whether it has been done. */
function filledCount(section, review) {
  const total = section.fields.length;
  const done = section.fields.filter(([k]) => {
    const v = review[k];
    return v !== undefined && v !== null && String(v).trim() !== '';
  }).length;
  return done ? `${done}/${total}` : null;
}
